import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { MainLayout } from "./components/layout/MainLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import CreateOrderPage from "./pages/CreateOrderPage";
import TasksPage from "./pages/TasksPage";
import TaskDetailPage from "./pages/TaskDetailPage";
import TaskDelegationPage from "./pages/TaskDelegationPage";
import MyTasksPage from "./pages/MyTasksPage";
import ItemsPage from "./pages/ItemsPage";
import CustomersPage from "./pages/CustomersPage";
import UsersPage from "./pages/UsersPage";
import ReportsPage from "./pages/ReportsPage";
import TaskTemplatesPage from "./pages/TaskTemplatesPage";
import CategoriesPage from "./pages/CategoriesPage";
import AdminSecurityPage from "./pages/AdminSecurityPage";
import DeletionRequestsPage from "./pages/DeletionRequestsPage";
import ILMPage from "./pages/ILMPage";
import TaskListDetailPage from "./pages/TaskListDetailPage";
import OrderEditRequestsPage from "./pages/OrderEditRequestsPage";
import ProductionLogPage from "./pages/ProductionLogPage";
import ProductionReportsPage from "./pages/ProductionReportsPage";
import TargetDateRequestsPage from "./pages/TargetDateRequestsPage";
import DevicePendingPage from "./pages/DevicePendingPage";
import DeviceRegistrationPage from "./pages/DeviceRegistrationPage";

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id during render (synchronous)
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register-device/:token" element={<DeviceRegistrationPage />} />
      
      {/* Device Pending - protected but outside main layout */}
      <Route
        path="/device-pending"
        element={
          <ProtectedRoute>
            <DevicePendingPage />
          </ProtectedRoute>
        }
      />
      
      {/* Protected Routes with Layout */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/orders/new" element={<CreateOrderPage />} />
        <Route path="/orders/:orderId" element={<OrderDetailPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/my" element={<MyTasksPage />} />
        <Route path="/tasks/detail" element={<TaskListDetailPage />} />
        <Route path="/tasks/my/detail" element={<TaskListDetailPage />} />
        <Route path="/tasks/delegate" element={<TaskDelegationPage />} />
        <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
        <Route path="/items" element={<ItemsPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/ilm" element={<ILMPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/admin/task-templates" element={<TaskTemplatesPage />} />
        <Route path="/admin/deletion-requests" element={<DeletionRequestsPage />} />
        <Route path="/admin/order-edit-requests" element={<OrderEditRequestsPage />} />
        <Route path="/admin/target-date-requests" element={<TargetDateRequestsPage />} />
        <Route path="/production-log" element={<ProductionLogPage />} />
        <Route path="/production-reports" element={<ProductionReportsPage />} />
        <Route path="/admin/security" element={<AdminSecurityPage />} />
        <Route path="/settings" element={<div className="p-6"><h1 className="font-heading text-3xl">Settings</h1><p className="text-muted-foreground mt-2">Settings page coming soon...</p></div>} />
      </Route>

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
